// components/Spirograph.js
import React, { useState, useEffect, useRef } from 'react';
import Canvas from './Canvas';
import Graphic from './Graphic';
import LayerControls from './LayerControls';
import '../style/Spirograph.css';
import TitleArea from './common/TitleArea';
import useResponsiveCanvasSize from '../hooks/useResponsiveCanvasSize';
import useScreenSize from '../hooks/useScreenSize';
import html2canvas from 'html2canvas';
import { getRandomColor } from '../utils/colorUtils'
import { useIsSquare } from "../hooks/useIsSquare";
import GelatoPricing from './GelatoPrice'; // keep your import name/path
import NavMenu from "./common/NavMenu";

const Spirograph = ({ isHomepage }) => {
  // --- only these two lines changed (initial state declarations)
  const [initialSettings, setInitialSettings] = useState(null); // will be loaded from REST or local JSON
  const [frameOptions, setFrameOptions] = useState(null); // will be loaded from REST or local JSON
  const [loadedGraphicId, setLoadedGraphicId] = useState('');

  // Rest of state exactly as before
  const [layerSettings, setLayerSettings] = useState([]);
  const [formattedDate, setFormattedDate] = useState('');
  const fixedCanvasSize = useResponsiveCanvasSize();
  const { isMobile, isSmallScreen } = useScreenSize();
  const [storedImage, setStoredImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const defaultPersonal = {
    showTitle: false,
    titleText: "Your Title",
    titleFontFamily: "Abril Fatface",
    titleFontSize: 42,
    showSubtitle: false,
    subtitleText: "Your sub title",
    subtitleFontFamily: "Abril Fatface",
    subtitleFontSize: 24,
    showLegend: false,
    legendText: "",
  };
  const defaultOther = {
    backgroundColor: undefined,
    useBleed: false,
    style: 'dark',
    size: '',
    material: 'paper',
    frame: 'none'
  }
  const defaultFrame = {
    material: "paper",
    size: '12x12',
    orientation: 'portrait',
    frame: 'none'
  }
  const [personalSettings, setPersonalSettings] = useState(defaultPersonal);
  const isSquare = useIsSquare(personalSettings);
  const [otherSettings, setOtherSettings] = useState(defaultOther);
  const [frameSettings, setFrameSettings] = useState(defaultFrame);
  const [svgSize, setSvgSize] = useState(800); // Default size
  const [step, setStep] = useState(1);
  const navItems = [
    { icon: "yourdate", label: 'Your Date' },
    { icon: "customize", label: 'Customize' },
    { icon: "personalize", label: 'Personalize' },
    { icon: "style", label: 'Style' },
    { icon: "frames", label: 'Frame' },
  ];

  const containerRef = useRef(null);

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({
      top: 0,
      behavior: 'smooth', // remove if you want instant jump
    });
  };

  const handleNext = () => {
    setStep((prev) => prev + 1);
    window.scrollTo({
      top: 0,
      behavior: 'smooth', // remove if you want instant jump
    });
  };

  const handleAddToCart = () => {
    console.log("add to cart hit");
  };


  // Add this inside the Spirograph component (paste after your existing edit_cart_key useEffect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const graphicid = params.get('graphicid');
    if (!graphicid) return; // nothing to do

    const fetchAndApplyGraphic = async () => {
      try {
        setLoading(true);

        // prefer rotateadate namespace, fallback to gelato-connect (alias)
        const endpoints = [
          `/wp-json/rotateadate/v1/fetch-graphic?id=${encodeURIComponent(graphicid)}`,
          `/wp-json/gelato-connect/v1/fetch-graphic?id=${encodeURIComponent(graphicid)}`,
        ];

        // send nonce if available (safe to include)
        const nonce = (window.RAD_CONFIG && window.RAD_CONFIG.wpNonce)
          || (window.gelatoConnect && window.gelatoConnect.nonce)
          || (window.wpApiSettings && window.wpApiSettings.nonce)
          || null;

        let json = null;
        let lastErr = null;

        for (const url of endpoints) {
          try {
            const res = await fetch(url, {
              method: 'GET',
              credentials: 'same-origin',
              headers: {
                ...(nonce ? { 'X-WP-Nonce': nonce } : {}),
              },
            });

            if (!res.ok) {
              // try next endpoint
              lastErr = `HTTP ${res.status} on ${url}`;
              continue;
            }

            json = await res.json();
            // normalize different possible response shapes:
            // - { success:true, data: {...} }
            // - { id:..., data: {...} }
            // - { data: {...} }
            // - raw payload object
            let payload = null;
            if (json === null) {
              lastErr = `Empty JSON from ${url}`;
              continue;
            }
            if (json.success === true && json.data) {
              payload = json.data;
            } else if (json.data) {
              payload = json.data;
            } else if (json.payload) {
              payload = json.payload;
            } else if (json.layerSettings || json.personalSettings || json.frameSettings || json.otherSettings) {
              // likely the raw payload
              payload = json;
            } else if (json.id && json.data) {
              payload = json.data;
            } else {
              // not recognized — treat whole json as payload
              payload = json;
            }

            if (payload) {
              console.log('Fetched graphic payload:', payload);
              if (payload.layerSettings) setLayerSettings(payload.layerSettings);
              else if (payload.layers) setLayerSettings(payload.layers);

              if (payload.formattedDate) setFormattedDate(payload.formattedDate);
              if (payload.personalSettings) setPersonalSettings(payload.personalSettings);
              if (payload.otherSettings) setOtherSettings(payload.otherSettings);
              if (payload.frameSettings) setFrameSettings(payload.frameSettings);

              if (payload.svg) {
                try { localStorage.setItem('restored_svg', payload.svg); } catch (e) { /* ignore */ }
              }

              // record loaded graphic id so child can populate inputs reliably
              setLoadedGraphicId(graphicid || (payload.id ? String(payload.id) : ''));

              // move user to final (view/share) step — still step 5 per your requirement
              setStep(5);

              window.scrollTo({ top: 0, behavior: 'smooth' });

              lastErr = null;
              break;
            }
            else {
              lastErr = `No payload found in response from ${url}`;
              continue;
            }
          } catch (err) {
            lastErr = err.message || String(err);
            console.warn('fetch graphic try failed', url, lastErr);
            continue;
          }
        }

        if (lastErr) {
          console.warn('Failed to fetch graphic:', lastErr);
        }
      } catch (err) {
        console.error('Error applying shared graphic:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAndApplyGraphic();
    // run once on mount
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------
  // Minimal change: load settings from WP REST or local JSON
  // ... (your existing loadSettings useEffect unchanged)
  useEffect(() => {
    let cancelled = false;

    const isLocalHost = () => {
      if (typeof window === 'undefined') return false;
      const host = window.location.hostname;
      return host === 'localhost' || host === '127.0.0.1';
    };

    async function fetchLocalJsons() {
      try {
        const [initMod, framesMod] = await Promise.all([
          import('../data/initialSettings.json'),
          import('../data/frameOptions.json')
        ]);
        return {
          init: initMod && (initMod.default || initMod),
          frames: framesMod && (framesMod.default || framesMod)
        };
      } catch (err) {
        console.warn('Local JSON import failed:', err);
        return { init: null, frames: null };
      }
    }

    async function loadSettings() {
      let initJson = null;
      let framesJson = null;

      if (isLocalHost()) {
        const local = await fetchLocalJsons();
        initJson = local.init;
        framesJson = local.frames;
      } else if(!loadedGraphicId){
        try {
          
          const [initRes, framesRes] = await Promise.all([
            fetch('/wp-json/gelato-connect/v1/initial-settings'),
            fetch('/wp-json/gelato-connect/v1/frame-options')
          ]);

          console.log('REST fetch results:', { initRes, framesRes });
          if (initRes && initRes.ok) initJson = await initRes.json();
          if (framesRes && framesRes.ok) framesJson = await framesRes.json();
        } catch (err) {
          console.warn('REST fetch failed (will try local JSON):', err);
        }

        if (!initJson || !framesJson) {
          const local = await fetchLocalJsons();
          initJson = initJson || local.init;
          framesJson = framesJson || local.frames;
        }

      }
      console.log('Final loaded settings:', { initJson, framesJson });
      if (cancelled) return;
      
      setInitialSettings(initJson || []);
      setFrameOptions(framesJson || { frames: [] });
      console.log('Set initialSettings', loadedGraphicId, layerSettings, initJson);
      const savedLayer = localStorage.getItem('spiroLayerSettings');
      if (savedLayer) {
        try {
          setLayerSettings(JSON.parse(savedLayer));
          setFormattedDate(localStorage.getItem('spiroFormattedDate') || '');
          personalSettings(localStorage.getItem('spiroPersonalSettings') || '');

          setStep(2);
        } catch (err) {
          console.warn('Failed to parse saved layer settings', err);
          setLayerSettings(initJson || []);
        }
      } else {
        setLayerSettings(initJson || []);
      }
    }

    loadSettings();
    return () => { cancelled = true; };
  }, [isHomepage]);
  // -------------------------

  // keep frameSettings orientation/size in sync with isSquare
  useEffect(() => {
    let orientationId = isSquare ? 'square' : 'portrait';
    let size = isSquare ? '12x12' : '12x16';
    setFrameSettings((prev) => ({ ...prev, orientation: orientationId, size: size }));
  }, [isSquare]);

  // Homepage randomization — run only after initialSettings is loaded
  useEffect(() => {
    if (!isHomepage || !initialSettings) return;

    // Run immediately once (optional but recommended)
    randomizeLayersForHomepage();

    const interval = setInterval(() => {
      randomizeLayersForHomepage();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval); // cleanup on unmount or change
  }, [isHomepage, initialSettings]);
  // Put in a useEffect (e.g. in Spirograph) — make sure you have set* setters in scope
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editCartKey = params.get('edit_cart_key');
    if (!editCartKey) return;

    const fetchAndApply = async () => {
      try {
        const nonce = (window.gelatoConnect && window.gelatoConnect.nonce) || (window.wpApiSettings && window.wpApiSettings.nonce) || null;
        const res = await fetch(`/wp-json/gelato-connect/v1/get-cart-item?cart_key=${encodeURIComponent(editCartKey)}`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            ...(nonce ? { 'X-WP-Nonce': nonce } : {})
          }
        });

        if (!res.ok) {
          console.warn('Could not fetch cart item for edit:', res.status);
          return;
        }

        const json = await res.json();
        if (!json.design_payload) {
          console.warn('No design payload found in cart item:', json);
          return;
        }

        const payload = json.design_payload;

        // Apply the payload into state (adjust keys to match exact payload shape saved earlier)
        if (payload.layerSettings) setLayerSettings(payload.layerSettings);
        if (payload.personalSettings) setPersonalSettings(payload.personalSettings);
        if (payload.otherSettings) setOtherSettings(payload.otherSettings);
        if (payload.frameSettings) setFrameSettings(payload.frameSettings);
        // if svg saved, you can keep it in memory for download or further usage
        // optionally restore capturedImage/thumbImage if available in payload
        if (payload.svg) {
          // optional: save svg in localStorage to reuse or show preview
          try { localStorage.setItem('restored_svg', payload.svg); } catch (e) { }
        }

        // Move user to the customize step so they can edit immediately (choose whichever step suits your UX)
        setStep(3);
        // close any overlays / scroll to canvas if needed
        window.scrollTo({ top: 0, behavior: 'smooth' });

      } catch (err) {
        console.error('Failed to load cart item for edit:', err);
      }
    };

    fetchAndApply();
  }, []); // run once on mount

  const randomizeLayersForHomepage = () => {
    const today = new Date();
    const day = today.getDate();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    const fixedRadii = [100, 80, 60];

    const source = Array.isArray(initialSettings) && initialSettings.length ? initialSettings : [
      { name: 'Layer 1', numShapes: month, shapeRadius: 100, colorData: { type: 'solid', color: '#111' } },
      { name: 'Layer 2', numShapes: day, shapeRadius: 80, colorData: { type: 'solid', color: '#222' } },
      { name: 'Layer 3', numShapes: year, shapeRadius: 60, colorData: { type: 'solid', color: '#333' } },
    ];

    const updatedSettings = source.map((layer, index) => {
      if (index === 0) return { ...layer, numShapes: month };
      if (index === 1) return { ...layer, numShapes: day };
      if (index === 2) return { ...layer, numShapes: year };
      return layer;
    });

    const randomized = updatedSettings.map((layer, i) => {
      const shapeTypes = ['circle', 'square', 'triangle', 'hexagon'];
      const shapeType = shapeTypes[Math.floor(Math.random() * shapeTypes.length)];
      const isGradient = isHomepage ? 1 : Math.random() < 0.5;
      const gradientType = Math.random() < 0.5 ? 'linear' : 'radial';
      const shapeRadius = isHomepage
        ? (fixedRadii[i] ?? 50)
        : Math.floor(Math.random() * (100 - 50 + 1)) + 50;
      const randOpp = i === 2 ? true : false;
      const colorData = isGradient
        ? {
          type: 'gradient',
          gradientType,
          color1: getRandomColor(randOpp),
          color2: getRandomColor(randOpp),
          degree: '0deg',
        }
        : {
          type: 'solid',
          color: getRandomColor(randOpp),
        };

      const updatedLayer = {
        ...layer,
        shapeRadius,
        shapeType,
        colorData,
        strokeWidth: layer.strokeWidth ?? 1,
      };

      return updatedLayer;
    });

    setLayerSettings(randomized);
  };

  const resetImage = () => {
    localStorage.removeItem('capturedImage');
    setStoredImage(null);
  };

  const getLayoutHeights = () => {
    if (!isMobile) {
      return {
        layerStyle: {},
        canvasStyle: {},
      };
    }

    const canvasHeight = fixedCanvasSize + (step === 4 ? 205 : 5);

    return {
      layerStyle:
        step === 3 || step === 4
          ? {
           /*  maxHeight: `calc(100vh - ${canvasHeight}px)`, */
            overflowY: 'auto',
          }
          : { overflowY: 'auto' },

      canvasStyle: {
        height: `${canvasHeight}px`,
      },
    };
  };
  const { layerStyle, canvasStyle } = getLayoutHeights();

  const handleLayerChange = (index, key, value) => {
    console.log('handleLayerChange', index, key, value);
    setLayerSettings((prev) =>
      prev.map((layer, i) =>
        i === index ? { ...layer, [key]: value } : layer
      )
    );
  };

  const handleSizeChange = (size) => {
    setSvgSize(Number(size));
  };

  // export svg string for saving in payload (non-destructive)
  const exportSVGString = () => {
    const svgElement = document.querySelector('svg');
    if (!svgElement) return null;

    // We return outerHTML, and also include width/height attributes reflecting svgSize
    const cloned = svgElement.cloneNode(true);
    cloned.setAttribute('width', svgSize);
    cloned.setAttribute('height', svgSize);
    const wrapper = document.createElement('div');
    wrapper.appendChild(cloned);
    const svgString = wrapper.innerHTML;
    return svgString;
  };

  const saveAsSVG = () => {
    const svgElement = document.querySelector('svg');
    if (!svgElement) {
      alert('SVG not found');
      return;
    }
    const scaleFactor = svgSize / 800; // Scale based on selected size
    const scaledShapes = svgElement.innerHTML.replace(/(cx|cy|r|x|y|width|height|points)="(\d+(\.\d+)?)"/g, (match, attr, value) => {
      return `${attr}="${Number(value) * scaleFactor}"`;
    });

    const svgString = `
      <svg width="${svgSize}" height="${svgSize}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgSize} ${svgSize}">
        ${scaledShapes}
      </svg>
    `;

    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `spirograph-${svgSize}x${svgSize}.svg`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const saveDraft = () => {
    localStorage.setItem('spirographDraft', JSON.stringify(layerSettings));
    alert('Draft saved!');
  };
  const resetToDraft = () => {
    const savedSettings = localStorage.getItem('spirographDraft');
    if (savedSettings) {
      setLayerSettings(JSON.parse(savedSettings));
    } else {
      setLayerSettings(initialSettings || []);
    }
  };


  // Minimal saveImageToLocalStorage - DO NOT persist full base64 images to localStorage
  const saveImageToLocalStorage = async () => {
    setLoading(true);
    resetImage();

    setTimeout(async () => {
      const element = containerRef.current;
      if (!element) {
        setLoading(false);
        return;
      }

      try {
        const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: null });
        const thumb = await html2canvas(element, { scale: 0.5, useCORS: true, backgroundColor: null });
        // keep data in memory only (avoid storing base64 strings in localStorage)
        const dataUrl = canvas.toDataURL('image/png'); // still available in memory if needed
        const thumbUrl = thumb.toDataURL('image/png');

        // DO NOT: localStorage.setItem('capturedImage', dataUrl);
        // DO NOT: localStorage.setItem('thumbImage', thumbUrl);
        // Instead, keep it in memory for immediate preview only:
        setStoredImage(thumbUrl);

        // Save only compact settings to localStorage (not full images)
        try {
          localStorage.setItem('spiroLayerSettings', JSON.stringify(layerSettings));
          localStorage.setItem('spiroPersonalSettings', JSON.stringify(personalSettings));
        } catch (e) {
          console.warn('Could not save settings to localStorage', e);
        }

        setLoading(false);
      } catch (error) {
        console.error('Failed to save image:', error);
        setLoading(false);
        alert('Something went wrong while saving the image.');
      }
    }, 100);
  };

  // Save only payload and meta — do not save thumbnails/base64 images
  const mySaveToLocalStorage = async (key, storageObject) => {
    try {
      // Remove any "thumbnail" property if present before saving
      const safeCopy = Object.assign({}, storageObject);
      if (safeCopy.thumbnail) delete safeCopy.thumbnail;

      localStorage.setItem(key, JSON.stringify(safeCopy));
      if (safeCopy.payload) {
        try {
          localStorage.setItem('spiroLayerSettings', JSON.stringify(safeCopy.payload.layerSettings || layerSettings || []));
          localStorage.setItem('spiroPersonalSettings', JSON.stringify(safeCopy.payload.personalSettings || personalSettings || {}));
        } catch (e) {
          console.warn('Could not save payload subkeys to localStorage', e);
        }
      }
      return true;
    } catch (err) {
      console.error('mySaveToLocalStorage failed', err);
      return false;
    }
  };

  /* ============================
     NEW: functions used by GelatoPricing component
     - myGetPayload() returns a serialisable payload the plugin needs
     - mySaveToLocalStorage(key, storageObject) persists the payload locally
     ============================ */
  const myGetPayload = async () => {
    // Build a compact payload with all settings + optional svg (string)
    try {
      const svg = exportSVGString(); // may be null, but helpful to save
      const payload = {
        layerSettings,
        personalSettings,
        otherSettings,
        frameSettings,
        svg,
        svgSize,
        createdAt: new Date().toISOString(),
      };
      return payload;
    } catch (err) {
      console.error('myGetPayload error', err);
      return {
        layerSettings,
        personalSettings,
        otherSettings,
        frameSettings,
        createdAt: new Date().toISOString(),
      };
    }
  };


  /* ============================ end new functions ============================ */

  return (
    <div className={`spiro-wrapper ${isHomepage ? 'forhome' : ''}`}>
      {loading && (
        <div className="saving-overlay">
          <div className="spinner" />
          <span>Loading your design!</span>
        </div>
      )}
      {isHomepage && (
        <div className="rotate-slow">
          <Graphic
            layerSettings={layerSettings}
          />
        </div>
      )}

      {(step < 6 && !isHomepage) && (
        <>
          <NavMenu
            navItems={navItems}
            step={step}
            setStep={setStep}
            formattedDate={formattedDate}
          // iconsBasePath="/custom/path/if/you/want/"
          />

          <div className={`layer-container step-${step}`} style={layerStyle}>
            {step === 2 && (
              <> <TitleArea title={'Customize Your Design'} subTitle={'Make the design your own by using our tools below. Change the colors, shapes and sizes to your preference. <br/> <br/> Or see what is possible by using our randomizer.'} forMobile={true} />
              </>
            )}
            {step === 3 && (
              <> <TitleArea title={'Personalize Your Design'} subTitle={'Add something special to personalize your design. Select a font, add your text and size to your preference.'} forMobile={true} />
              </>
            )}
            <LayerControls
              layerSettings={layerSettings}
              onLayerChange={handleLayerChange}
              onSizeChange={handleSizeChange}
              onSave={saveAsSVG}
              setLayerSettings={setLayerSettings}
              step={step}
              setStep={setStep}
              initialSettings={initialSettings || []}
              personalSettings={personalSettings}
              setPersonalSettings={setPersonalSettings}
              formattedDate={formattedDate}
              setFormattedDate={setFormattedDate}
              onGenerateImage={saveImageToLocalStorage}
              otherSettings={otherSettings}
              setOtherSettings={setOtherSettings}
              frameSettings={frameSettings}
              setFrameSettings={setFrameSettings}
              frameOptions={frameOptions || { frames: [] }}
              isSquare={isSquare}
              loadedGraphicId={loadedGraphicId}
            />
          </div>

          {/* IMPORTANT: include "frame-container" class so the Gelato pricing component (and its DOM snapshot) can find the element */}
          <div className={`canvas-container frame-container ${frameSettings.material === 'canvas' ? 'frame-material-canvas' : 'frame-material-paper'}`} ref={containerRef}
            style={{
              backgroundImage: frameSettings.image || '',
              height: (frameSettings.material === 'canvas' && !isMobile) ? '100%' : '',
              /* padding: (frameSettings.material === 'canvas' && isMobile) ? '0px' : '' */
            }}
          >
            <Canvas
              layerSettings={layerSettings}
              setLayerSettings={setLayerSettings}
              step={step}
              personalSettings={personalSettings}
              otherSettings={otherSettings}
              frameSettings={frameSettings}
              frameOptions={frameOptions || { frames: [] }}
              formattedDate={formattedDate}
            />
          </div>

          {step === 5 && (
            <GelatoPricing
              frameSettings={frameSettings}
              step={step}
              getGraphicPayload={myGetPayload}
              saveGraphicLocally={mySaveToLocalStorage}
              canvasProps={{
                layerSettings,
                setLayerSettings,
                step,
                personalSettings,
                otherSettings,
                frameSettings,
                frameOptions: frameOptions || { frames: [] }
              }}
              setLoading={setLoading}
            />
          )}

        </>
      )}
      {(!isHomepage && isMobile && step != 1) && (
        <div className="step-navigation"
          style={{
           marginBottom: step === 5 ? '70px' : '' 
          }}
        >
          {/* Back button */}
          {step > 1 && (
            <button
              className="btn-back"
              onClick={handleBack}
            >
              Back
            </button>
          )}

          {/* Next / Add to Cart */}
          {step !== 5 && (
            <button
              className="btn-next"
              onClick={handleNext}
            >
              Next
            </button>
          )}

          {/* {step === 5 && (
            <button
              className="btn-add-to-cart"
              onClick={handleAddToCart}
            >
              Add to Cart
            </button>
          )} */}
        </div>
      )}

    </div>
  );
};

export default Spirograph;
